import { createSlice } from '@reduxjs/toolkit';
import { graphqlApi } from '../../api'
import { openBypassPopupBlocker } from "../../../../util/functions";

const initialState = {
    pendingDownloadsByReceiptId: {},
    pendingDownloadsByUuid: {},
    pendingDownloadsByRequestedDocumentKey: {},
    
    // Open All variables
    isOpeningAll: false,
    openOrderByRequestedDocumentKey: [], // Order to open documents in
    timeoutId: undefined,
};

const documentsSlice = createSlice({
    name: 'applicant-detail-documents',
    initialState,
    reducers: {
        updateEvents(state, action) {
            // This is called when EventBridge event arrives with receiptId
            // SECURITY: UUID is never sent over AppSync Events to maintain privacy between same-role users
            const { receiptId } = action.payload.event;
            console.log('updateEvents called with receiptId:', receiptId);
            
            // Look up the pending download by receiptId
            const downloadInfo = state.pendingDownloadsByReceiptId[receiptId];
            
            if (!downloadInfo) {
                console.warn('No pending download found for receiptId:', receiptId);
                return;
            }
            
            // The UUID should already be in downloadInfo from the initial fetchDocumentUrl response
            if (downloadInfo.uuid) {
                // Ensure it's also in the UUID map
                state.pendingDownloadsByUuid[downloadInfo.uuid] = downloadInfo;
                console.log('Event received for receiptId:', receiptId, 'uuid:', downloadInfo.uuid);
            } else {
                console.warn('No uuid in downloadInfo for receiptId:', receiptId);
            }
        },
        
        openAllDocuments(state, { payload }) {
            const { openOrder, timeoutId } = payload;
            state.isOpeningAll = true;
            state.openOrderByRequestedDocumentKey = openOrder;
            state.timeoutId = timeoutId;
        },
        
        resetOpenAllDocuments(state) {
            state.isOpeningAll = false;
            state.openOrderByRequestedDocumentKey = [];
            state.pendingDownloadsByReceiptId = {};
            state.pendingDownloadsByUuid = {};
            state.pendingDownloadsByRequestedDocumentKey = {};
            state.timeoutId = undefined;
        },
    },
    extraReducers: (builder) => {
        builder.addMatcher(
            graphqlApi.endpoints.getDocuments.matchFulfilled,
            (state, action) => {
                const { meta, payload } = action;
                const { documentId, documentName } = meta?.arg?.originalArgs ?? {};
                
                const requestedDocumentKey = `${documentId}#${documentName}`;
                const uuid = payload?.data?.uuid;
                const status = payload?.data?.status;
                let receiptId = payload?.data?.receiptId;
                const url = payload?.data?.url;
                
                console.log('getDocuments.matchFulfilled - status:', status, 'uuid:', uuid, 'receiptId:', receiptId, 'url:', url);
                
                let downloadInfo = { requestedDocumentKey, uuid, status, receiptId, url };
                
                // Handle 'ready' status - document is cached
                if (status === 'ready' && url) {
                    console.log('Document is ready (cached), opening immediately');
                    // Single link clicked, received a cached URL, open it immediately
                    if (!state.isOpeningAll) { openBypassPopupBlocker(url); return; }
                    
                    // Opening all? Use uuid as key and mark as ready
                    const key = uuid || url;
                    downloadInfo.ready = true;
                    state.pendingDownloadsByReceiptId[key] = downloadInfo;
                    if (uuid) state.pendingDownloadsByUuid[uuid] = downloadInfo;
                    state.pendingDownloadsByRequestedDocumentKey = getPendingDownloadsByRequestedDocumentKey(state.pendingDownloadsByReceiptId);
                    handleOpenAllFlow(state, key);
                    return;
                }
                
                // Handle 'pending' status - document is being fetched, wait for event
                if (status === 'pending' && receiptId) {
                    console.log('Document is pending, storing with receiptId:', receiptId, 'uuid:', uuid);
                    state.pendingDownloadsByReceiptId[receiptId] = downloadInfo;
                    if (uuid) {
                        state.pendingDownloadsByUuid[uuid] = downloadInfo;
                        console.log('Also stored by uuid:', uuid);
                    }
                    state.pendingDownloadsByRequestedDocumentKey = getPendingDownloadsByRequestedDocumentKey(state.pendingDownloadsByReceiptId);
                    return;
                }
                
                // Legacy handling for old API responses (no status field)
                if (receiptId === null && url) {
                    console.log('Legacy response: receiptId is null, url present');
                    // Single link clicked, received a cached URL, open it immediately, bypass slice entirely
                    if (!state.isOpeningAll) { openBypassPopupBlocker(url); return; }
                    
                    // Opening all? Replace null receiptId with url, and preset some values so we're not waiting for an event later
                    receiptId = url;
                    downloadInfo.receiptId = url;
                    downloadInfo.ready = true;
                }
                
                state.pendingDownloadsByReceiptId[receiptId] = downloadInfo;
                state.pendingDownloadsByRequestedDocumentKey = getPendingDownloadsByRequestedDocumentKey(state.pendingDownloadsByReceiptId);
                if (state.isOpeningAll && downloadInfo.ready) {
                    handleOpenAllFlow(state, receiptId); // In the case that all files are cached, need to handle this here
                }  
            }
        );
        builder.addMatcher(
            graphqlApi.endpoints.getDocuments.matchRejected,
            (state, action) => {
                if (!state.isOpeningAll) { return; }
                
                // When opening all, we need to keep track of failures
                const { meta } = action ?? {};
                const { documentId, documentName } = meta?.arg?.originalArgs ?? {};
                const requestedDocumentKey = `${documentId}#${documentName}`;
                
                // Track failures -- mark as ready in order to allow open all to open what it can
                state.pendingDownloadsByReceiptId[requestedDocumentKey] = { failed: true, ready: true, requestedDocumentKey };
                state.pendingDownloadsByRequestedDocumentKey = getPendingDownloadsByRequestedDocumentKey(state.pendingDownloadsByReceiptId);
                handleOpenAllFlow(state, requestedDocumentKey);
            }
        );
        builder.addMatcher(
            graphqlApi.endpoints.getDocumentStatus.matchFulfilled,
            (state, action) => {
                const { payload } = action;
                const uuid = payload?.data?.uuid;
                const status = payload?.data?.status;
                const url = payload?.data?.url;
                
                console.log('getDocumentStatus.matchFulfilled - uuid:', uuid, 'status:', status, 'url:', url);
                
                if (!uuid) { 
                    console.warn('No uuid in getDocumentStatus response');
                    return; 
                }
                
                const downloadInfo = state.pendingDownloadsByUuid[uuid];
                if (!downloadInfo) { 
                    console.warn('No pending download found for uuid:', uuid);
                    console.log('Available uuids:', Object.keys(state.pendingDownloadsByUuid));
                    return; 
                }
                
                console.log('Found downloadInfo for uuid:', uuid, downloadInfo);
                
                // Update with the URL from status check
                if (status === 'ready' && url) {
                    downloadInfo.url = url;
                    downloadInfo.status = status;
                    downloadInfo.ready = true;
                    
                    console.log('Document ready, url:', url);
                    
                    // Update in both maps
                    if (downloadInfo.receiptId) {
                        state.pendingDownloadsByReceiptId[downloadInfo.receiptId] = downloadInfo;
                    }
                    
                    state.pendingDownloadsByRequestedDocumentKey = getPendingDownloadsByRequestedDocumentKey(state.pendingDownloadsByReceiptId);
                    
                    // Handle opening the document
                    if (state.isOpeningAll) {
                        console.log('Opening document as part of Open All');
                        handleOpenAllFlow(state, downloadInfo.receiptId || uuid);
                    } else {
                        console.log('Opening document immediately');
                        openBypassPopupBlocker(url);
                        delete state.pendingDownloadsByReceiptId[downloadInfo.receiptId];
                        delete state.pendingDownloadsByUuid[uuid];
                        state.pendingDownloadsByRequestedDocumentKey = getPendingDownloadsByRequestedDocumentKey(state.pendingDownloadsByReceiptId);
                    }
                } else if (status === 'error') {
                    console.error('Document status returned error');
                    // Handle error status
                    downloadInfo.failed = true;
                    downloadInfo.ready = true;
                    if (state.isOpeningAll) {
                        handleOpenAllFlow(state, downloadInfo.receiptId || uuid);
                    }
                } else {
                    console.log('Document status is still pending or unknown:', status);
                }
            }
        );
    }
});

// Helper functions
function handleOpenAllFlow(state, receiptId) {
    state.pendingDownloadsByReceiptId[receiptId].ready = true;
    
    // Wait for the rest to finish downloading (all must be ready)
    const receipts = Object.values(state.pendingDownloadsByReceiptId);
    if (receipts.some(pending => !pending.ready) || receipts.length < state.openOrderByRequestedDocumentKey.length) { return; }

    // Open them in order
    const orderToUse = JSON.parse(JSON.stringify(state.openOrderByRequestedDocumentKey));
    const urlsToOpen = JSON.parse(JSON.stringify(state.pendingDownloadsByRequestedDocumentKey));

    orderToUse.forEach?.(requestedDocumentKey => {
        const { url } = urlsToOpen[requestedDocumentKey];
        if (url) { openBypassPopupBlocker(url); }
    });

    // Clean up
    clearTimeout(state.timeoutId);
    state.isOpeningAll = false;
    state.pendingDownloadsByReceiptId = {};
    state.pendingDownloadsByRequestedDocumentKey = {};
    state.timeoutId = undefined;
}

function getPendingDownloadsByRequestedDocumentKey(pendingDownloads) {
    let pdbrdk = {};
    
    for (const pendingDownload of Object.values(pendingDownloads)) {
        pdbrdk[pendingDownload.requestedDocumentKey] = clone(pendingDownload);
    }
    
    return pdbrdk;
}

function clone(o) { return JSON.parse(JSON.stringify(o)); }

// Exports
export const { updateEvents, openAllDocuments, resetOpenAllDocuments } = documentsSlice.actions;

export const selectDocumentsData = (state) => state.documentsSlice

export default documentsSlice.reducer;